import { useEffect, useRef } from 'react';
import { playSoundEffect } from '../hooks/useSoundEffects';

/**
 * Hook to detect when strategems are played and play appropriate sound effects
 * - shrink.mp3 for strategems that shrink units
 * - grow.mp3 for strategems that grow units
 * - destroy.mp3 for strategems that destroy units (55 = vein.mp3, 59 = fracture_minds.mp3 custom)
 */
export const useStrategemSoundEffects = (gameState, userId) => {
  const previousCompletedStrategemsRef = useRef(new Set());
  const isFirstRenderRef = useRef(true);

  // Mapping of strategem IDs to their sound effects
  const strategemSoundMapping = {
    // Shrink strategems
    shrink: new Set([180, 183]), // Touch of darkness, Growing Darkness (53 = neurotox.mp3 custom)
    
    // Grow strategems
    grow: new Set([182, 224, 227]), // Gift of the Rift, Divine Favor, Praise the Gods (62 = hive_surge.mp3 custom)
    
    // Destroy / direct-damage strategems (including general damage)
    destroy: new Set([
      51,  // Culling Protocol
      54,  // Repurposed Warhead
      59,  // Fracture Minds — custom fracture_minds.mp3 above
      // 60 — custom betrayal.mp3 above (Spread the Rot)
      63,  // Pulse Shockwave
      112, // Storm of Missiles
      116, // Field Dominator
      179, // Surprise Attack (can destroy)
      181, // Tactical Breach
      197  // Unethical Experiment
    ])
  };

  // Custom sound mappings for specific strategem IDs
  const customSoundMapping = {
    60: '/sounds/betrayal.mp3',   // Spread the Rot — same sting as The Betrayal (341)
    115: '/sounds/stinger.mp3',   // Stinger Unit Alpha
    226: '/sounds/mummy.mp3',     // Embalm
    313: '/sounds/duplicate.mp3', // Duplicate
    316: '/sounds/missile_barrage.mp3', // Missile Barrage
    317: '/sounds/missile.mp3',   // Guided Missile
    319: '/sounds/fire.mp3',       // Inferno Drop
    321: '/sounds/missile.mp3',    // Precision Strike
    52: '/sounds/flesh_mimic.mp3', // Flesh Mimic
    53: '/sounds/neurotox.mp3', // Neurotox Pulse
    55: '/sounds/vein.mp3', // Vein Sever
    56: '/sounds/heal.mp3', // Viral Echo
    185: '/sounds/heal.mp3', // Shadow Bargain (dual general effect — same sting as heal)
    188: '/sounds/heal.mp3', // Essence Siphon
    220: '/sounds/blood.mp3', // Plague of Water turned to Blood
    58: '/sounds/bloom.mp3', // Plague Bloom
    61: '/sounds/bloom.mp3', // Cradle of Decay (same as Plague Bloom)
    184: '/sounds/bloom.mp3', // Orbital Riftstrike (same as Cradle / Plague Bloom)
    221: '/sounds/gnats.mp3', // Plague of Lice and Gnats
    187: '/sounds/surge.mp3', // Quantum Surge
    62: '/sounds/hive_surge.mp3', // Hive Surge
    59: '/sounds/fracture_minds.mp3', // Fracture Minds
    111: '/sounds/kinetron.mp3', // Kinetron Blast
    112: '/sounds/storm_of_missiles.mp3', // Storm of Missiles
    114: '/sounds/loadout.mp3', // Tactical Loadout
    186: '/sounds/reposition.mp3', // Phase Traverse
  };

  useEffect(() => {
    if (!gameState?.state || !userId) return;

    // Skip detection on first render
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      
      // Build initial set of completed strategems
      const allPlayerIds = gameState.players || Object.keys(gameState.state);
      allPlayerIds.forEach(playerId => {
        const player = gameState.state[playerId];
        if (player?.completedOrders) {
          player.completedOrders.forEach(strategem => {
            if (strategem && strategem.instanceId && strategem.type === 'Strategem') {
              const key = `${playerId}_${strategem.instanceId}`;
              previousCompletedStrategemsRef.current.add(key);
            }
          });
        }
      });
      return;
    }

    const allPlayerIds = gameState.players || Object.keys(gameState.state);
    const newlyPlayedStrategems = [];

    // Detect newly completed strategems (moved from hand to completedOrders)
    allPlayerIds.forEach(playerId => {
      const player = gameState.state[playerId];
      if (!player?.completedOrders) return;

      player.completedOrders.forEach(strategem => {
        if (strategem && strategem.instanceId && strategem.type === 'Strategem') {
          const key = `${playerId}_${strategem.instanceId}`;
          
          // If this strategem wasn't in completedOrders before, it was just played
          if (!previousCompletedStrategemsRef.current.has(key)) {
            newlyPlayedStrategems.push({
              strategem,
              playerId,
              key
            });
            console.log(`🎯 STRATEGEM PLAYED: ${strategem.name} (ID: ${strategem.id}) by player ${playerId}`);
          }
        }
      });
    });

    // Play sounds for newly played strategems
    if (newlyPlayedStrategems.length > 0) {
      newlyPlayedStrategems.forEach(({ strategem, playerId }, index) => {
        const delay = index * 100; // Stagger sounds if multiple strategems played simultaneously
        
        setTimeout(() => {
          const strategemId = strategem.id;
          const strategemIdNum = Number(strategemId);
          let soundPath = null;

          // Check custom sound mapping first (takes priority). Keys are numeric; id may be string from state.
          const customPath =
            customSoundMapping[strategemId] ??
            (Number.isFinite(strategemIdNum) ? customSoundMapping[strategemIdNum] : undefined);
          if (customPath) {
            soundPath = customPath;
            console.log(`🔊 Playing custom sound for strategem ${strategem.name} (ID: ${strategemId}): ${soundPath}`);
          } else if (strategemSoundMapping.shrink.has(strategemId) || strategemSoundMapping.shrink.has(strategemIdNum)) {
            soundPath = '/sounds/shrink.mp3';
            console.log(`🔊 Playing shrink sound for strategem ${strategem.name} (ID: ${strategemId})`);
          } else if (strategemSoundMapping.grow.has(strategemId) || strategemSoundMapping.grow.has(strategemIdNum)) {
            soundPath = '/sounds/grow.mp3';
            console.log(`🔊 Playing grow sound for strategem ${strategem.name} (ID: ${strategemId})`);
          } else if (strategemSoundMapping.destroy.has(strategemId) || strategemSoundMapping.destroy.has(strategemIdNum)) {
            soundPath = '/sounds/destroy.mp3';
            console.log(`🔊 Playing destroy sound for strategem ${strategem.name} (ID: ${strategemId})`);
          }

          if (soundPath) {
            try {
              playSoundEffect(soundPath, 0.6); // Play at 60% volume
              console.log(`✅ Strategem sound triggered successfully: ${soundPath}`);
            } catch (error) {
              console.error(`❌ Error playing strategem sound:`, error);
            }
          } else {
            console.log(`⚠️ No sound mapping for strategem ${strategem.name} (ID: ${strategemId})`);
          }
        }, delay);
      });
    }

    // Update the previous completed strategems set
    const currentCompletedStrategems = new Set();
    allPlayerIds.forEach(playerId => {
      const player = gameState.state[playerId];
      if (player?.completedOrders) {
        player.completedOrders.forEach(strategem => {
          if (strategem && strategem.instanceId && strategem.type === 'Strategem') {
            const key = `${playerId}_${strategem.instanceId}`;
            currentCompletedStrategems.add(key);
          }
        });
      }
    });
    previousCompletedStrategemsRef.current = currentCompletedStrategems;
  }, [gameState, userId]);
};

